"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { bQuietS, Chevron as ChevronDown, field, Popover } from "./ui";

// Поле даты со СВОИМ календарём.
//
// 🔴 Почему не `<input type="date">`, который стоял здесь раньше. Правка Марата
// 08.08: нативный календарь в нашем интерфейсе выглядит чужим — синий системный
// прямоугольник поверх спокойного тёмного экрана, со своими шрифтами, своими
// кнопками «Удалить/Сегодня» и своим языком. Он ещё и разный в каждом браузере:
// в Chrome одно, в Safari другое, а на Windows третье. Раз рабочее место
// собрано из своих примитивов, календарь обязан быть таким же.
//
// Поле при этом ОСТАЁТСЯ ТЕКСТОВЫМ и принимает набор руками: «08.08.2026»,
// «8.8.26», «08082026». Бухгалтеру, который вбивает дату документа с бумажки,
// быстрее набрать восемь цифр, чем листать месяцы мышью.
//
// Значение наружу — всегда ISO `ГГГГ-ММ-ДД`, ровно как отдавал нативный input:
// так замена не трогает ни одного обработчика и ни одного действия API.

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const MONTHS = [
  "январь", "февраль", "март", "апрель", "май", "июнь",
  "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь",
];
const MONTHS_SHORT = [
  "янв", "фев", "мар", "апр", "май", "июн",
  "июл", "авг", "сен", "окт", "ноя", "дек",
];

/** ISO-дата сегодняшнего дня по местному времени (а не по UTC — иначе вечером «завтра»). */
export function todayIso(): string {
  return isoOf(new Date());
}

function isoOf(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** ISO → «08.08.2026». Пустая строка остаётся пустой. */
export function ruDate(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

/**
 * Разобрать то, что набрал человек.
 *
 * Принимаем и точки, и слеши, и голые цифры; год из двух цифр читаем как
 * двадцать первый век. Всё, что не разобралось, — `null`: поле тогда покажет
 * прежнее значение, а не подставит выдуманную дату.
 */
export function parseRuDate(raw: string): string | null {
  const s = raw.trim();
  if (!s) return "";
  let d: number, m: number, y: number;

  // Сначала РАЗДЕЛИТЕЛИ, и только потом голые цифры.
  //
  // Обратный порядок ломался на «8.8.2026»: цифр там шесть, и разбор «по
  // длине» читал их как 88-й день 20-го месяца 26-го года. Поймано тестом —
  // ровно тот случай, когда правило «где ошибка стоит денег, там тест» окупается
  // в первый же день.
  const parts = s.split(/[.\-/\s]+/).filter(Boolean);
  if (parts.length === 3) {
    d = Number(parts[0]);
    m = Number(parts[1]);
    y = Number(parts[2]);
    if (y < 100) y += 2000;
  } else {
    const digits = s.replace(/\D/g, "");
    if (digits.length === 8) {
      d = Number(digits.slice(0, 2));
      m = Number(digits.slice(2, 4));
      y = Number(digits.slice(4));
    } else if (digits.length === 6) {
      d = Number(digits.slice(0, 2));
      m = Number(digits.slice(2, 4));
      y = 2000 + Number(digits.slice(4));
    } else {
      return null;
    }
  }
  if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2200) return null;
  // Проверка существования дня: 31 февраля в календаре нет, и молча превращать
  // его в 3 марта нельзя — в накладной это была бы другая дата.
  const dt = new Date(y, m - 1, d);
  if (dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return isoOf(dt);
}

function CalendarGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} className="h-4 w-4" aria-hidden>
      <path d="M3 6a2 2 0 012-2h14a2 2 0 012 2v13a2 2 0 01-2 2H5a2 2 0 01-2-2zM16 2v4M8 2v4M3 10h18" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Боковые стрелки «предыдущий/следующий» — это НЕ шеврон раскрытия из ui.tsx,
// поэтому живут своим рисунком; общий шеврон импортирован как ChevronDown.
function Chevron({ dir }: { dir: "left" | "right" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4" aria-hidden>
      <path d={dir === "left" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function DateField({
  value,
  onChange,
  min,
  max,
  placeholder = "дд.мм.гггг",
  disabled,
  ariaLabel,
  className,
  clearable = true,
}: {
  /** ISO `ГГГГ-ММ-ДД` или пустая строка. */
  value: string;
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  clearable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => ruDate(value));
  // Какой месяц показан в календаре. Отдельно от значения: листать месяцы можно
  // и не выбирая дату.
  const [view, setView] = useState(() => value || todayIso());
  const [pickingMonth, setPickingMonth] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Значение сменилось снаружи (загрузились данные, сбросили форму) — текст
  // обязан догнать, иначе поле показывает старое.
  useEffect(() => {
    setText(ruDate(value));
    if (value) setView(value);
  }, [value]);

  const [vy, vm] = useMemo(() => {
    const [y, m] = (view || todayIso()).split("-").map(Number);
    return [y, m];
  }, [view]);

  const days = useMemo(() => buildGrid(vy, vm), [vy, vm]);
  const today = todayIso();

  function commitText(raw: string) {
    const iso = parseRuDate(raw);
    if (iso === null) {
      // Не разобрали — возвращаем то, что было. Молча очищать поле нельзя:
      // человек потеряет дату из-за одной опечатки.
      setText(ruDate(value));
      return;
    }
    if (iso && !inRange(iso, min, max)) {
      setText(ruDate(value));
      return;
    }
    onChange(iso);
    setText(ruDate(iso));
    if (iso) setView(iso);
  }

  function pick(iso: string) {
    if (!inRange(iso, min, max)) return;
    onChange(iso);
    setText(ruDate(iso));
    setView(iso);
    setOpen(false);
  }

  function shiftMonth(delta: number) {
    const d = new Date(vy, vm - 1 + delta, 1);
    setView(isoOf(d));
  }

  return (
    <div className={`relative ${className ?? ""}`} ref={boxRef}>
      <input
        className={`${field} pr-11`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commitText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitText(text);
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        inputMode="numeric"
        aria-label={ariaLabel ?? "Дата"}
      />
      <button
        type="button"
        className="absolute right-1.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-[var(--op-faint)] transition-colors hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)] disabled:opacity-40"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Открыть календарь"
      >
        <CalendarGlyph />
      </button>

      <Popover
        open={open}
        anchorRef={boxRef}
        onClose={() => {
          setOpen(false);
          setPickingMonth(false);
        }}
        width={296}
        label="Выбор даты"
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          {/* Стрелка ЗНАЧКОМ внутри кнопки, а не фоновой картинкой поля:
              фон-стрелка `selectChevron` рисуется у правого края и на кнопке
              без запаса справа ложится прямо на слово «август». */}
          <button
            type="button"
            className="flex h-8 items-center gap-1.5 rounded-full bg-[var(--op-surface-2)] pl-3 pr-2.5 text-[13px] font-semibold text-[var(--op-fg)] transition-colors hover:bg-[var(--op-ghost)]"
            onClick={() => setPickingMonth((v) => !v)}
            aria-label="Выбрать месяц и год"
          >
            <span className="first-letter:uppercase">{MONTHS[vm - 1]} {vy}</span>
            <ChevronDown open={pickingMonth} />
          </button>
          {!pickingMonth && (
            <div className="flex gap-1">
              <button
                type="button"
                className="grid h-8 w-8 place-items-center rounded-full text-[var(--op-muted)] transition-colors hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]"
                onClick={() => shiftMonth(-1)}
                aria-label="Предыдущий месяц"
              >
                <Chevron dir="left" />
              </button>
              <button
                type="button"
                className="grid h-8 w-8 place-items-center rounded-full text-[var(--op-muted)] transition-colors hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]"
                onClick={() => shiftMonth(1)}
                aria-label="Следующий месяц"
              >
                <Chevron dir="right" />
              </button>
            </div>
          )}
        </div>

        {pickingMonth ? (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                className="grid h-8 w-8 place-items-center rounded-full text-[var(--op-muted)] hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]"
                onClick={() => setView(isoOf(new Date(vy - 1, vm - 1, 1)))}
                aria-label="Предыдущий год"
              >
                <Chevron dir="left" />
              </button>
              <span className="text-[15px] font-semibold tabular-nums text-[var(--op-fg)]">{vy}</span>
              <button
                type="button"
                className="grid h-8 w-8 place-items-center rounded-full text-[var(--op-muted)] hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]"
                onClick={() => setView(isoOf(new Date(vy + 1, vm - 1, 1)))}
                aria-label="Следующий год"
              >
                <Chevron dir="right" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {MONTHS_SHORT.map((label, i) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    setView(isoOf(new Date(vy, i, 1)));
                    setPickingMonth(false);
                  }}
                  className={[
                    "h-9 rounded-full text-[13px] font-semibold transition-colors",
                    i + 1 === vm
                      ? "bg-[var(--op-primary)] text-[var(--op-primary-ink)]"
                      : "bg-[var(--op-surface-2)] text-[var(--op-muted)] hover:text-[var(--op-fg)]",
                  ].join(" ")}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-1 grid grid-cols-7 gap-1">
              {WEEKDAYS.map((w) => (
                <div key={w} className="text-center text-[11px] font-medium text-[var(--op-faint)]">
                  {w}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {days.map((iso) => {
                const d = Number(iso.slice(8));
                const own = Number(iso.slice(5, 7)) === vm;
                const selected = iso === value;
                const isToday = iso === today;
                const allowed = inRange(iso, min, max);
                return (
                  <button
                    key={iso}
                    type="button"
                    disabled={!allowed}
                    onClick={() => pick(iso)}
                    className={[
                      "h-9 rounded-full text-[13px] tabular-nums transition-colors",
                      selected
                        ? "bg-[var(--op-primary)] font-semibold text-[var(--op-primary-ink)]"
                        : isToday
                          // Сегодня — кантом, а не заливкой: заливок на экране
                          // должно быть ровно столько, сколько выбранных дат.
                          // Графит, не акцент (S1-А, 23.09): статус дня.
                          ? "text-[var(--op-fg)] ring-1 ring-inset ring-[var(--op-fg)]"
                          : own
                            ? "text-[var(--op-fg)] hover:bg-[var(--op-ghost)]"
                            // Дни соседних месяцев приглушены, но кликабельны:
                            // «31 июля» в июльской сетке — обычная дата.
                            : "text-[var(--op-faint)] hover:bg-[var(--op-ghost)]",
                      allowed ? "" : "cursor-not-allowed opacity-30 hover:bg-transparent",
                    ].join(" ")}
                  >
                    {d}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 flex items-center justify-between">
              <button
                type="button"
                className={bQuietS}
                onClick={() => pick(today)}
                disabled={!inRange(today, min, max)}
              >
                Сегодня
              </button>
              {clearable && value && (
                <button
                  type="button"
                  className={bQuietS}
                  onClick={() => {
                    onChange("");
                    setText("");
                    setOpen(false);
                  }}
                >
                  Очистить
                </button>
              )}
            </div>
          </>
        )}
      </Popover>
    </div>
  );
}

function inRange(iso: string, min?: string, max?: string): boolean {
  if (!iso) return true;
  if (min && iso < min) return false;
  if (max && iso > max) return false;
  return true;
}

/**
 * Шесть недель с понедельника — всегда одинаковой высоты.
 *
 * Сетка переменной высоты дёргает всё, что стоит ниже, при листании месяцев:
 * февраль в невисокосный год укладывается в четыре строки, март в шесть.
 */
function buildGrid(y: number, m: number): string[] {
  const first = new Date(y, m - 1, 1);
  // getDay(): 0 — воскресенье. У нас неделя с понедельника.
  const shift = (first.getDay() + 6) % 7;
  const start = new Date(y, m - 1, 1 - shift);
  return Array.from({ length: 42 }, (_, i) => isoOf(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)));
}
